import { useMemo } from 'react'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'
import type { UseFiltersPayload } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditImageLibraryModal from '@modals/CreateOrEditImageLibraryModal'
import CreateOrEditImageModal from '@modals/CreateOrEditImageModal'

import type { ImageLibraryModel, ImageModel } from '@models/types'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    showCreateOrEditImageLibraryModal: () => void,
  },
}

const defaultState = {
  showCreateOrEditImageLibraryModal: false,
  showCreateOrEditImageModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditImageLibraryModal: {
      closeModal: () => setState({ showCreateOrEditImageLibraryModal: false }),
      createImageLibrary: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateImageLibrary: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditImageModal: {
      closeModal: () => setState({ showCreateOrEditImageModal: false }),
      deleteImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      setCoverImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateOrEditImageLibraryModal, showCreateOrEditImageModal } = state

  const filtersPayload = useFilters({ persistFilters: true })
  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = filtersPayload

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditImageLibraryModal: (payload: { imageLibrary: ImageLibraryModel }) => {
        setModalData('CreateOrEditImageLibraryModal', payload)
        setState({ showCreateOrEditImageLibraryModal: true })
      },
      showCreateOrEditImageModal: (payload: { image: ImageModel }) => {
        setModalData('CreateOrEditImageModal', payload)
        setState({ showCreateOrEditImageModal: true })
      },
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt]) // modalContext dependency required if layering modals

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateOrEditImageLibraryModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditImageLibraryModal', setState)}
          showModal={showCreateOrEditImageLibraryModal}
        />

        <CreateOrEditImageModal
          callbacks={callbacks('CreateOrEditImageModal', setState)}
          showModal={showCreateOrEditImageModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
